import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { getApiUrl } from "../../api";

export default function FindPw() {
  const location = useLocation();
  const lockedState = location.state as { locked?: boolean; message?: string } | null;
  const [name, setName] = useState("");
  const [loginId, setLoginId] = useState("");
  const [email, setEmail] = useState("");
  const [nameMsg, setNameMsg] = useState("");
  const [loginIdMsg, setLoginIdMsg] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const inputStyle = (hasError: boolean): React.CSSProperties => ({
    borderColor: hasError ? "#ef4444" : "#e5e7eb",
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setNameMsg("");
    setLoginIdMsg("");
    setEmailMsg("");

    let hasError = false;
    if (!name.trim()) { setNameMsg("이름을 입력해주세요."); hasError = true; }
    if (!loginId.trim()) { setLoginIdMsg("아이디를 입력해주세요."); hasError = true; }
    if (!email.trim()) { setEmailMsg("이메일을 입력해주세요."); hasError = true; }
    if (hasError) return;

    setLoading(true);
    try {
      const res = await fetch(getApiUrl("/marubit/api/member/reset_password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), login_id: loginId.trim(), email: email.trim() }),
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(true);
      } else if (res.status === 404) {
        setNameMsg("이름, 아이디 또는 이메일이 올바르지 않습니다.");
      } else if (res.status === 500) {
        setEmailMsg("이메일 전송에 실패했습니다. 잠시 후 다시 시도해주세요.");
      } else {
        setNameMsg(data.detail || "오류가 발생했습니다.");
      }
    } catch (err) {
      setNameMsg("서버 연결 오류: " + (err as Error).message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center bg-[#f5f2f0]">
      <div className="w-full max-w-[420px] bg-white rounded-2xl shadow-[0_8px_32px_rgba(136,114,103,0.1)] px-9 py-10">

        {/* 헤더 */}
        <div className="mb-7">
          <h2 className="text-[20px] font-bold text-[#3d2f2a] mb-1">비밀번호 찾기</h2>
          <p className="text-[13px] text-[#999]">가입 정보를 입력하면 임시 비밀번호를 이메일로 보내드립니다.</p>
        </div>

        {/* 계정 잠금 안내 (로그인 5회 실패로 이동된 경우) */}
        {lockedState?.locked && (
          <div className="mb-5 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-[13px] text-red-600 leading-relaxed">
            {lockedState.message || "비밀번호를 5회 잘못 입력하여 계정이 잠겼습니다."}
            <br />
            임시 비밀번호를 발급받아 재설정하면 잠금이 해제됩니다.
          </div>
        )}

        {!success ? (
          <form onSubmit={handleSubmit} className="flex flex-col">

            {/* 이름 */}
            <div className="mb-4">
              <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">
                이름
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="이름을 입력하세요"
                className="w-full h-11 px-[14px] border-[1.5px] rounded-lg text-[14px] outline-none box-border transition-colors duration-150"
                style={inputStyle(!!nameMsg)}
                onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
                onBlur={e => (e.currentTarget.style.borderColor = nameMsg ? "#ef4444" : "#e5e7eb")}
              />
              {nameMsg && <span className="text-[12px] text-red-500 mt-1 block">{nameMsg}</span>}
            </div>

            {/* 아이디 */}
            <div className="mb-4">
              <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">
                아이디
              </label>
              <input
                type="text"
                value={loginId}
                onChange={e => setLoginId(e.target.value)}
                placeholder="아이디를 입력하세요"
                className="w-full h-11 px-[14px] border-[1.5px] rounded-lg text-[14px] outline-none box-border transition-colors duration-150"
                style={inputStyle(!!loginIdMsg)}
                onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
                onBlur={e => (e.currentTarget.style.borderColor = loginIdMsg ? "#ef4444" : "#e5e7eb")}
              />
              {loginIdMsg && <span className="text-[12px] text-red-500 mt-1 block">{loginIdMsg}</span>}
            </div>

            {/* 이메일 */}
            <div className="mb-6">
              <label className="block text-[13px] font-semibold text-[#887267] mb-[6px]">
                이메일
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="가입 시 등록한 이메일을 입력하세요"
                className="w-full h-11 px-[14px] border-[1.5px] rounded-lg text-[14px] outline-none box-border transition-colors duration-150"
                style={inputStyle(!!emailMsg)}
                onFocus={e => (e.currentTarget.style.borderColor = "#887267")}
                onBlur={e => (e.currentTarget.style.borderColor = emailMsg ? "#ef4444" : "#e5e7eb")}
              />
              {emailMsg && <span className="text-[12px] text-red-500 mt-1 block">{emailMsg}</span>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className={[
                "w-full h-[46px] text-white border-none rounded-lg text-[15px] font-bold transition-colors duration-150",
                loading ? "bg-[#b8a39b] cursor-not-allowed" : "bg-[#887267] cursor-pointer hover:bg-[#6b5248]",
              ].join(" ")}
            >
              {loading ? "전송 중..." : "임시 비밀번호 발급"}
            </button>

          </form>
        ) : (
          /* 성공 화면 */
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 bg-[#f0ebe8] rounded-full flex items-center justify-center mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17L4 12" stroke="#887267" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <p className="text-[15px] font-semibold text-[#3d2f2a] mb-2">임시 비밀번호가 발송되었습니다</p>
            <p className="text-[13px] text-[#999] mb-6 leading-relaxed">
              <span className="font-semibold text-[#887267]">{email}</span>으로<br />
              임시 비밀번호를 발송했습니다.<br />
              로그인 후 반드시 비밀번호를 변경해주세요.
            </p>
            <button
              onClick={() => navigate("/auth/login")}
              className="w-full h-[46px] bg-[#887267] text-white border-none rounded-lg text-[15px] font-bold cursor-pointer hover:bg-[#6b5248] transition-colors duration-150"
            >
              로그인하러 가기
            </button>
          </div>
        )}

        {/* 하단 링크 */}
        {!success && (
          <div className="flex justify-center items-center mt-5 gap-1">
            <Link to="/auth/login" className="px-3 text-[13px] text-[#666] no-underline">로그인</Link>
            <span className="text-gray-200">|</span>
            <Link to="/auth/find_id" className="px-3 text-[13px] text-[#666] no-underline">아이디 찾기</Link>
            <span className="text-gray-200">|</span>
            <Link to="/auth/register" className="px-3 text-[13px] text-[#887267] font-semibold no-underline">회원가입</Link>
          </div>
        )}
      </div>
    </div>
  );
}